import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Space,
  Skeleton,
  Tag,
  Empty,
  Progress,
  Table,
  Tabs,
  Badge,
  message,
} from 'antd';
import {
  CreditCardOutlined,
  HistoryOutlined,
  RocketOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
  MailOutlined,
  WhatsAppOutlined,
  SendOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosConfig from '../../../../../config/axiosConfig';
import { useWaba } from '../../../../../context/wabaContext';
import { Skeleton as BoneSkeleton } from 'boneyard-js/react';
import './WalletPage.css';

const { Text } = Typography;

/* ──────────────────────────────────────────────
   Skeleton Loader
   ────────────────────────────────────────────── */
const BillingPageSkeleton = () => (
  <div className="wp-container">
    <div className="wp-header">
      <div>
        <Skeleton.Input active size="large" style={{ width: 220, marginBottom: 8 }} />
        <br />
        <Skeleton.Input active size="small" style={{ width: 280 }} />
      </div>
      <Space>
        <Skeleton.Button active size="large" shape="round" style={{ width: 100 }} />
        <Skeleton.Button active size="large" shape="round" style={{ width: 140 }} />
      </Space>
    </div>
    <Card className="wp-balance-card" style={{ marginBottom: 24 }}>
      <Skeleton active paragraph={{ rows: 3 }} />
    </Card>
    <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
      <Col xs={24} lg={12}>
        <Card className="wp-card"><Skeleton active paragraph={{ rows: 3 }} /></Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card className="wp-card"><Skeleton active paragraph={{ rows: 3 }} /></Card>
      </Col>
    </Row>
  </div>
);

/* ──────────────────────────────────────────────
   Main Component — Plan-Based Billing & Usage
   ────────────────────────────────────────────── */
const WalletPage = () => {
  const navigate = useNavigate();

  /* ── Plan Status State ────────────────────── */
  const [planData, setPlanData] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [planError, setPlanError] = useState(false);
  const [activeTab, setActiveTab] = useState('status');

  /* ── History States ───────────────────────── */
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentPagination, setPaymentPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  const [emailUsage, setEmailUsage] = useState([]);
  const [emailUsageLoading, setEmailUsageLoading] = useState(false);
  const [emailPagination, setEmailPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  const [waUsage, setWaUsage] = useState([]);
  const [waUsageLoading, setWaUsageLoading] = useState(false);
  const [waPagination, setWaPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  // Rate limiting
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const lastRefreshRef = useRef(0);
  const refreshInProgressRef = useRef(false);
  const REFRESH_COOLDOWN_MS = 3000;

  // WABA
  const { hasWabaDetails, loadingWabaDetails: wabaLoading, wabaFetchError, refreshWabaDetails } = useWaba();
  const wabaNotReady = !wabaLoading && !hasWabaDetails && !wabaFetchError;

  /* ── Fetch Plan Status ──────────────────────── */
  const fetchPlanStatus = useCallback(async () => {
    try {
      setPlanLoading(true);
      setPlanError(false);
      const res = await axiosConfig.get('/wallet/plan-status');
      const entity = res.data?.entity;
      if (entity) {
        setPlanData(entity);
      } else {
        // Fallback: try /wallet/balance for backwards compat
        const balRes = await axiosConfig.get('/wallet/balance');
        const ent = balRes.data?.entity;
        if (ent) {
          setPlanData(ent);
        } else {
          setPlanData({ has_active_plan: false });
        }
      }
    } catch (error) {
      console.error('Error fetching plan status:', error);
      setPlanError(true);
    } finally {
      setPlanLoading(false);
    }
  }, []);

  /* ── Fetch Payment History ────────────────── */
  const fetchPaymentHistory = useCallback(async (page = 1, pageSize = 15) => {
    try {
      setPaymentLoading(true);
      const offset = (page - 1) * pageSize;
      const res = await axiosConfig.get('/wallet/payment-history', { params: { limit: pageSize, offset } });
      const entity = res.data?.entity;
      if (entity) {
        setPaymentHistory(entity.purchases || []);
        setPaymentPagination(prev => ({ ...prev, current: page, total: entity.pagination?.total || 0 }));
      }
    } catch (err) {
      console.error('Error fetching payment history:', err);
    } finally {
      setPaymentLoading(false);
    }
  }, []);

  /* ── Fetch Email Usage ────────────────────── */
  const fetchEmailUsage = useCallback(async (page = 1, pageSize = 15) => {
    try {
      setEmailUsageLoading(true);
      const offset = (page - 1) * pageSize;
      const res = await axiosConfig.get('/wallet/email-usage', { params: { limit: pageSize, offset } });
      const entity = res.data?.entity;
      if (entity) {
        setEmailUsage(entity.campaigns || []);
        setEmailPagination(prev => ({ ...prev, current: page, total: entity.pagination?.total || 0 }));
      }
    } catch (err) {
      console.error('Error fetching email usage:', err);
    } finally {
      setEmailUsageLoading(false);
    }
  }, []);

  /* ── Fetch WhatsApp Usage ─────────────────── */
  const fetchWaUsage = useCallback(async (page = 1, pageSize = 15) => {
    try {
      setWaUsageLoading(true);
      const offset = (page - 1) * pageSize;
      const res = await axiosConfig.get('/wallet/whatsapp-usage', { params: { limit: pageSize, offset } });
      const entity = res.data?.entity;
      if (entity) {
        setWaUsage(entity.campaigns || []);
        setWaPagination(prev => ({ ...prev, current: page, total: entity.pagination?.total || 0 }));
      }
    } catch (err) {
      console.error('Error fetching WA usage:', err);
    } finally {
      setWaUsageLoading(false);
    }
  }, []);

  /* ── Initial Load ─────────────────────────── */
  useEffect(() => {
    fetchPlanStatus();
    fetchPaymentHistory();
    fetchEmailUsage();
    fetchWaUsage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load tab data on tab switch (if data was cleared or paginated)
  useEffect(() => {
    if (activeTab === 'payments' && paymentHistory.length === 0) fetchPaymentHistory();
    if (activeTab === 'email-usage' && emailUsage.length === 0) fetchEmailUsage();
    if (activeTab === 'wa-usage' && waUsage.length === 0) fetchWaUsage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-refresh: 5 min interval + focus
  useEffect(() => {
    const intervalId = setInterval(() => fetchPlanStatus(), 5 * 60 * 1000);
    const handleFocus = () => fetchPlanStatus();
    const handleVisibility = () => { if (!document.hidden) fetchPlanStatus(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchPlanStatus]);

  /* ── Refresh handler ──────────────────────── */
  const handleRefresh = useCallback(async () => {
    const now = Date.now();
    if (refreshInProgressRef.current) return;
    if (now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) {
      message.warning('Please wait a moment before refreshing again');
      return;
    }
    refreshInProgressRef.current = true;
    lastRefreshRef.current = now;
    setRefreshCooldown(true);

    try {
      await Promise.all([fetchPlanStatus(), refreshWabaDetails()]);
      if (activeTab === 'payments') await fetchPaymentHistory();
      if (activeTab === 'email-usage') await fetchEmailUsage();
      if (activeTab === 'wa-usage') await fetchWaUsage();
    } finally {
      refreshInProgressRef.current = false;
      setTimeout(() => setRefreshCooldown(false), REFRESH_COOLDOWN_MS);
    }
  }, [refreshWabaDetails, fetchPlanStatus, fetchPaymentHistory, fetchEmailUsage, fetchWaUsage, activeTab]);

  /* ── Computed ──────────────────────────────── */
  const hasActivePlan = planData?.has_active_plan === true;
  const email = planData?.email || { limit: 0, used: 0, remaining: 0 };
  const whatsapp = planData?.whatsapp || { campaign_limit: 0, campaigns_used: 0, campaigns_remaining: 0, is_unlimited: false };
  const daysRemaining = planData?.days_remaining ?? 0;

  /* ── Helpers ──────────────────────────────── */
  const fmtDate = (str) => {
    if (!str) return 'N/A';
    return new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

  const getEmailPct = () => email.limit > 0 ? Math.round((email.used / email.limit) * 100) : 0;
  const getWaPct = () => whatsapp.campaign_limit > 0 ? Math.round((whatsapp.campaigns_used / whatsapp.campaign_limit) * 100) : 0;

  const emailBarColor = () => {
    if (email.remaining <= 0) return '#ff4d4f';
    if (email.limit > 0 && email.remaining / email.limit < 0.10) return '#faad14';
    return 'var(--wp-accent)';
  };
  const waBarColor = () => {
    if (!whatsapp.is_unlimited && whatsapp.campaigns_remaining !== null && whatsapp.campaigns_remaining <= 0) return '#ff4d4f';
    if (!whatsapp.is_unlimited && whatsapp.campaign_limit > 0 && whatsapp.campaigns_remaining / whatsapp.campaign_limit < 0.10) return '#faad14';
    return '#52c41a';
  };

  /* ── Status badge helpers ───────────────── */
  const paymentStatusBadge = (status) => {
    const map = { active: 'success', expired: 'default', cancelled: 'error' };
    return <Tag color={map[status] || 'default'}>{(status || 'unknown').charAt(0).toUpperCase() + (status || '').slice(1)}</Tag>;
  };

  const emailCampaignStatusBadge = (status) => {
    const map = { completed: 'success', completed_with_errors: 'warning', failed: 'error', sending: 'processing', pending: 'default' };
    const labels = { completed: 'Completed', completed_with_errors: 'Partial', failed: 'Failed', sending: 'Sending', pending: 'Pending' };
    return <Tag color={map[status] || 'default'}>{labels[status] || status}</Tag>;
  };

  /* ──────────────────────────────────────────────
     Render: Plan Status (main overview)
     ────────────────────────────────────────────── */
  const renderPlanStatus = () => {
    if (planError) return (
      <Card className="wp-card" style={{ textAlign: 'center', padding: 40 }}>
        <Text type="danger">Failed to load plan status</Text>
        <br />
        <Button type="primary" onClick={fetchPlanStatus} style={{ marginTop: 12 }}>Retry</Button>
      </Card>
    );

    return (
      <BoneSkeleton name="wallet-page" loading={planLoading} fallback={<BillingPageSkeleton />}>
      <div className="wp-container">

        {/* Expiry Warning Banners */}
        {hasActivePlan && daysRemaining <= 1 && (
          <div className="wp-alert wp-alert-critical">
            <span className="wp-alert-icon"><ExclamationCircleOutlined /></span>
            <span className="wp-alert-text"><strong>CRITICAL</strong><span className="wp-alert-sep">&mdash;</span>Your plan expires today! Renew immediately to avoid service disruption.</span>
          </div>
        )}
        {hasActivePlan && daysRemaining > 1 && daysRemaining <= 7 && (
          <div className="wp-alert wp-alert-warning">
            <span className="wp-alert-icon"><WarningOutlined /></span>
            <span className="wp-alert-text"><strong>WARNING</strong><span className="wp-alert-sep">&mdash;</span>Your plan expires in {daysRemaining} days. Consider renewing soon.</span>
          </div>
        )}

        {/* Email Quota Exhausted */}
        {hasActivePlan && email.remaining <= 0 && (
          <div className="wp-alert wp-alert-critical" style={{ marginTop: 8 }}>
            <span className="wp-alert-icon"><MailOutlined /></span>
            <span className="wp-alert-text"><strong>Email Quota Exhausted</strong><span className="wp-alert-sep">&mdash;</span>Upgrade your plan or wait for the next billing cycle.</span>
          </div>
        )}

        {/* WA Quota Exhausted */}
        {hasActivePlan && !whatsapp.is_unlimited && whatsapp.campaigns_remaining !== null && whatsapp.campaigns_remaining <= 0 && (
          <div className="wp-alert wp-alert-critical" style={{ marginTop: 8 }}>
            <span className="wp-alert-icon"><WhatsAppOutlined /></span>
            <span className="wp-alert-text"><strong>WhatsApp Campaign Quota Exhausted</strong><span className="wp-alert-sep">&mdash;</span>Upgrade your plan or wait for the next billing cycle.</span>
          </div>
        )}

        {/* No Active Plan State */}
        {!hasActivePlan && (
          <Card className="wp-balance-card" bordered={false}>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text style={{ fontSize: 16, color: 'var(--wp-text-secondary)' }}>No Active Plan</Text>}
              >
                <Text style={{ display: 'block', marginBottom: 16, color: 'var(--wp-text-muted)' }}>
                  Purchase a plan to start sending email and WhatsApp campaigns.
                </Text>
                <Button type="primary" size="large" icon={<RocketOutlined />} onClick={() => navigate('/dashboard/pricing')} className="wp-btn-primary">
                  Purchase a Plan
                </Button>
              </Empty>
            </div>
          </Card>
        )}

        {/* Active Plan Info Card — Compact */}
        {hasActivePlan && (
          <Card className="wp-balance-card" bordered={false}>
            <div className="wp-balance-inner">
              <div className="wp-balance-left">
                <div className="wp-balance-icon-wrap">
                  <CreditCardOutlined className="wp-balance-icon" />
                </div>
                <div className="wp-balance-info">
                  <Text className="wp-balance-label">Active Plan</Text>
                  <div className="wp-balance-amount">
                    <span className="wp-amount" style={{ fontSize: 28 }}>{planData.display_name || planData.plan_name || 'Plan'}</span>
                    <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginLeft: 8 }}>Active</Tag>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
                    <Text className="wp-balance-expiry"><CalendarOutlined /> Billing: {planData.billing_cycle || 'N/A'}</Text>
                    <Text className="wp-balance-expiry"><ClockCircleOutlined /> Renews: {fmtDate(planData.expires_at)}</Text>
                    <Text className="wp-balance-expiry" style={{ color: daysRemaining <= 7 ? '#ff4d4f' : undefined }}>
                      <ExclamationCircleOutlined /> Expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
                    </Text>
                  </div>
                </div>
              </div>
              <div className="wp-balance-right">
                <Space size="middle">
                  <Button
                    icon={<ReloadOutlined spin={refreshCooldown} />}
                    onClick={handleRefresh}
                    disabled={refreshCooldown}
                    className="wp-btn-secondary"
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    icon={<RocketOutlined />}
                    onClick={() => navigate('/dashboard/pricing')}
                    className="wp-btn-primary"
                  >
                    Upgrade Plan
                  </Button>
                </Space>
              </div>
            </div>
          </Card>
        )}

        {/* Quota Cards Row */}
        {hasActivePlan && (
          <Row gutter={[16, 16]} className="wp-section">
            {/* Email Quota */}
            <Col xs={24} lg={12}>
              <Card className="wp-card" bordered={false} bodyStyle={{ padding: '20px 24px' }}>
                <div className="wp-card-header" style={{ marginBottom: 12 }}>
                  <div className="wp-card-header-left">
                    <MailOutlined className="wp-card-header-icon wp-icon-blue" />
                    <Text strong className="wp-card-title">Email Quota</Text>
                  </div>
                  {email.remaining <= 0 ? (
                    <Tag color="error">Exhausted</Tag>
                  ) : email.limit > 0 && email.remaining / email.limit < 0.10 ? (
                    <Tag color="warning">Low (&lt;10%)</Tag>
                  ) : (
                    <Tag color="success">Healthy</Tag>
                  )}
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <Text strong style={{ fontSize: 24, color: 'var(--wp-text-primary)' }}>{fmtNum(email.remaining)}</Text>
                    <Text style={{ color: 'var(--wp-text-muted)', alignSelf: 'flex-end', fontSize: 13 }}>of {fmtNum(email.limit)}</Text>
                  </div>
                  <Progress
                    percent={getEmailPct()}
                    showInfo={false}
                    strokeColor={emailBarColor()}
                    trailColor="var(--wp-border)"
                    size="small"
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <Text style={{ fontSize: 11, color: 'var(--wp-text-muted)' }}>{fmtNum(email.used)} used</Text>
                    <Text style={{ fontSize: 11, color: 'var(--wp-text-muted)' }}>{email.limit > 0 ? (100 - getEmailPct()) : 0}% remaining</Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* WhatsApp Campaign Quota */}
            <Col xs={24} lg={12}>
              <Card className="wp-card" bordered={false} bodyStyle={{ padding: '20px 24px' }}>
                <div className="wp-card-header" style={{ marginBottom: 12 }}>
                  <div className="wp-card-header-left">
                    <WhatsAppOutlined className="wp-card-header-icon" style={{ color: '#25D366', background: '#ecfdf5' }} />
                    <Text strong className="wp-card-title">WhatsApp Campaigns</Text>
                  </div>
                  {whatsapp.is_unlimited ? (
                    <Tag color="purple">Unlimited</Tag>
                  ) : whatsapp.campaigns_remaining !== null && whatsapp.campaigns_remaining <= 0 ? (
                    <Tag color="error">Exhausted</Tag>
                  ) : whatsapp.campaign_limit > 0 && whatsapp.campaigns_remaining / whatsapp.campaign_limit < 0.10 ? (
                    <Tag color="warning">Low (&lt;10%)</Tag>
                  ) : (
                    <Tag color="success">Healthy</Tag>
                  )}
                </div>
                <div>
                  {whatsapp.is_unlimited ? (
                    <div>
                      <Text strong style={{ fontSize: 24, color: 'var(--wp-text-primary)' }}>Unlimited</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12, color: 'var(--wp-text-muted)' }}>
                          {fmtNum(whatsapp.campaigns_used)} campaign{whatsapp.campaigns_used !== 1 ? 's' : ''} sent this cycle
                        </Text>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Text strong style={{ fontSize: 24, color: 'var(--wp-text-primary)' }}>{fmtNum(whatsapp.campaigns_remaining)}</Text>
                        <Text style={{ color: 'var(--wp-text-muted)', alignSelf: 'flex-end', fontSize: 13 }}>of {fmtNum(whatsapp.campaign_limit)}</Text>
                      </div>
                      <Progress
                        percent={getWaPct()}
                        showInfo={false}
                        strokeColor={waBarColor()}
                        trailColor="var(--wp-border)"
                        size="small"
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: 'var(--wp-text-muted)' }}>{fmtNum(whatsapp.campaigns_used)} used</Text>
                        <Text style={{ fontSize: 11, color: 'var(--wp-text-muted)' }}>{whatsapp.campaign_limit > 0 ? (100 - getWaPct()) : 0}% remaining</Text>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Plan Features — Compact inline grid */}
        {hasActivePlan && planData.features && planData.features.length > 0 && (
          <Card className="wp-card" bordered={false} bodyStyle={{ padding: '16px 24px' }} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <InfoCircleOutlined style={{ color: '#f59e0b', fontSize: 15 }} />
              <Text strong style={{ fontSize: 14 }}>Plan Features</Text>
            </div>
            <Row gutter={[12, 4]}>
              {planData.features.map((feature, idx) => (
                <Col xs={24} sm={12} md={8} key={idx}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13 }} />
                    <Text style={{ fontSize: 12, color: '#475569' }}>{feature}</Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {/* Quick Actions */}
        <Row gutter={[20, 20]} className="wp-section">
          <Col xs={24} md={8}>
            <Card className="wp-action-card" hoverable onClick={() => setActiveTab('payments')}>
              <div className="wp-action-icon wp-action-icon-purple"><HistoryOutlined /></div>
              <div className="wp-action-text">
                <Text strong className="wp-action-title">Payment History</Text>
                <Text className="wp-action-desc">View all plan purchase records</Text>
              </div>
              <ArrowRightOutlined className="wp-action-arrow" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card className="wp-action-card" hoverable onClick={() => setActiveTab('email-usage')}>
              <div className="wp-action-icon wp-action-icon-blue"><MailOutlined /></div>
              <div className="wp-action-text">
                <Text strong className="wp-action-title">Email Usage</Text>
                <Text className="wp-action-desc">View email campaigns sent this cycle</Text>
              </div>
              <ArrowRightOutlined className="wp-action-arrow" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card className="wp-action-card" hoverable onClick={() => setActiveTab('wa-usage')}>
              <div className="wp-action-icon wp-action-icon-green"><WhatsAppOutlined /></div>
              <div className="wp-action-text">
                <Text strong className="wp-action-title">WhatsApp Usage</Text>
                <Text className="wp-action-desc">View WhatsApp campaigns sent this cycle</Text>
              </div>
              <ArrowRightOutlined className="wp-action-arrow" />
            </Card>
          </Col>
        </Row>

        {/* How Plans Work */}
        <Card className="wp-card wp-info-card" bordered={false}>
          <div className="wp-card-header" style={{ marginBottom: 20 }}>
            <div className="wp-card-header-left">
              <InfoCircleOutlined className="wp-card-header-icon wp-icon-orange" />
              <Text strong className="wp-card-title">How Plans Work</Text>
            </div>
          </div>
          <Row gutter={[20, 20]}>
            {[
              { step: '01', icon: <CreditCardOutlined />, title: 'Choose a Plan', desc: 'Pick Premium or Enterprise. Each plan includes email quota and WhatsApp campaign limits per billing cycle.', accent: 'var(--wp-accent)' },
              { step: '02', icon: <SendOutlined />, title: 'Send Campaigns', desc: 'Each email sent counts against your email quota. Each WhatsApp campaign counts as one campaign usage.', accent: '#8b5cf6' },
              { step: '03', icon: <EyeOutlined />, title: 'Track Usage', desc: 'Monitor your remaining quotas in real time. Failed emails are automatically restored to your quota.', accent: '#10b981' },
            ].map(item => (
              <Col xs={24} md={8} key={item.step}>
                <div className="wp-info-item">
                  <span className="wp-info-step">{item.step}</span>
                  <div className="wp-info-icon" style={{ color: item.accent, background: item.accent + '14' }}>
                    {item.icon}
                  </div>
                  <Text strong className="wp-info-title">{item.title}</Text>
                  <Text className="wp-info-desc">{item.desc}</Text>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      </div>
      </BoneSkeleton>
    );
  };

  /* ──────────────────────────────────────────────
     Render: Payment History Tab
     ────────────────────────────────────────────── */
  const paymentColumns = [
    {
      title: 'Plan', dataIndex: 'display_name', key: 'plan', width: 120,
      render: (name, r) => <Text strong>{name || r.plan_name || 'Plan'}</Text>,
    },
    {
      title: 'Cycle', dataIndex: 'billing_cycle', key: 'cycle', width: 90,
      render: (c) => <Tag>{c || 'N/A'}</Tag>,
    },
    {
      title: 'Amount', dataIndex: 'amount_paid', key: 'amount', width: 100,
      render: (a) => <Text strong style={{ color: '#1890ff' }}>₹{Number(a || 0).toLocaleString('en-IN')}</Text>,
    },
    {
      title: 'Period', key: 'period', width: 180,
      render: (_, r) => <Text style={{ fontSize: 12 }}>{fmtDate(r.starts_at)} – {fmtDate(r.expires_at)}</Text>,
    },
    {
      title: 'Emails Used', key: 'emails', width: 130,
      render: (_, r) => <Text>{fmtNum(r.emails_used)} / {fmtNum(r.email_limit)}</Text>,
    },
    {
      title: 'WA Campaigns', key: 'wa', width: 140,
      render: (_, r) => r.is_unlimited_whatsapp
        ? <Text>{fmtNum(r.whatsapp_campaigns_used)} / <Tag color="purple" style={{ margin: 0 }}>Unlimited</Tag></Text>
        : <Text>{fmtNum(r.whatsapp_campaigns_used)} / {fmtNum(r.whatsapp_campaign_limit)}</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: paymentStatusBadge,
    },
  ];

  /* ──────────────────────────────────────────────
     Render: Email Usage Tab
     ────────────────────────────────────────────── */
  const emailUsageColumns = [
    {
      title: 'Campaign', dataIndex: 'name', key: 'name', width: 180,
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Subject', dataIndex: 'subject', key: 'subject', width: 200, ellipsis: true,
    },
    {
      title: 'Recipients', dataIndex: 'total_recipients', key: 'recipients', width: 100,
      render: (n) => <Text>{fmtNum(n)}</Text>,
    },
    {
      title: 'Sent', dataIndex: 'sent_count', key: 'sent', width: 80,
      render: (n) => <Tag className="wp-tag-neutral">{fmtNum(n)}</Tag>,
    },
    {
      title: 'Delivered', dataIndex: 'delivered_count', key: 'delivered', width: 90,
      render: (n) => <Tag className="wp-tag-neutral">{fmtNum(n)}</Tag>,
    },
    {
      title: 'Failed', dataIndex: 'failed_count', key: 'failed', width: 80,
      render: (n) => n > 0 ? <Tag className="wp-tag-neutral">{fmtNum(n)}</Tag> : <Text style={{ color: 'var(--wp-text-muted)' }}>0</Text>,
    },
    {
      title: 'Opened', dataIndex: 'opened_count', key: 'opened', width: 80,
      render: (n) => fmtNum(n),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: emailCampaignStatusBadge,
    },
    {
      title: 'Sent At', dataIndex: 'sent_at', key: 'sent_at', width: 130,
      render: fmtDate,
    },
  ];

  /* ──────────────────────────────────────────────
     Render: WhatsApp Usage Tab
     ────────────────────────────────────────────── */
  const waUsageColumns = [
    {
      title: 'Campaign', dataIndex: 'campaignname', key: 'name', width: 180,
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Template', dataIndex: 'templatename', key: 'template', width: 160,
      render: (t) => <Tag className="wp-tag-subtle">{t || 'N/A'}</Tag>,
    },
    {
      title: 'Category', dataIndex: 'messagecategory', key: 'category', width: 110,
      render: (c) => <Tag className="wp-tag-subtle">{c || 'N/A'}</Tag>,
    },
    {
      title: 'Total Contacts', dataIndex: 'totalcontacts', key: 'totalcontacts', width: 110,
      render: (n) => fmtNum(n),
    },
    {
      title: 'Success', dataIndex: 'successcount', key: 'success', width: 90,
      render: (n) => <Tag className="wp-tag-neutral">{fmtNum(n)}</Tag>,
    },
    {
      title: 'Failed', dataIndex: 'failcount', key: 'failed', width: 80,
      render: (n) => n > 0 ? <Tag className="wp-tag-neutral">{fmtNum(n)}</Tag> : <Text style={{ color: 'var(--wp-text-muted)' }}>0</Text>,
    },
    {
      title: 'Sent At', dataIndex: 'createtime', key: 'createtime', width: 130,
      render: fmtDate,
    },
  ];

  /* ── Build Tab Items ────────────────────────── */
  const tabItems = [
    {
      key: 'status',
      label: (<Space><CreditCardOutlined /><span>Plan Status</span></Space>),
      children: renderPlanStatus(),
    },
    {
      key: 'payments',
      label: (<Space><HistoryOutlined /><span>Payment History</span><Badge count={paymentPagination.total} showZero overflowCount={999} color="#64748b" /></Space>),
      children: (
        <Card className="wp-card" bordered={false}>
          <Table
            size="small"
            className="wp-compact-table"
            columns={paymentColumns}
            dataSource={paymentHistory}
            loading={paymentLoading}
            rowKey="purchase_id"
            pagination={{ ...paymentPagination, showSizeChanger: true, showTotal: (total) => `${total} records` }}
            onChange={(p) => { setPaymentPagination(prev => ({ ...prev, current: p.current, pageSize: p.pageSize })); fetchPaymentHistory(p.current, p.pageSize); }}
            scroll={{ x: 900 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payment history" /> }}
          />
        </Card>
      ),
    },
    {
      key: 'email-usage',
      label: (<Space><MailOutlined /><span>Email Usage</span><Badge count={emailPagination.total} showZero overflowCount={999} color="#64748b" /></Space>),
      children: (
        <Card className="wp-card" bordered={false}>
          <Table
            size="small"
            className="wp-compact-table"
            columns={emailUsageColumns}
            dataSource={emailUsage}
            loading={emailUsageLoading}
            rowKey="id"
            pagination={{ ...emailPagination, showSizeChanger: true, showTotal: (total) => `${total} campaigns` }}
            onChange={(p) => { setEmailPagination(prev => ({ ...prev, current: p.current, pageSize: p.pageSize })); fetchEmailUsage(p.current, p.pageSize); }}
            scroll={{ x: 1000 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No email campaigns this cycle" /> }}
          />
        </Card>
      ),
    },
    {
      key: 'wa-usage',
      label: (<Space><WhatsAppOutlined /><span>WhatsApp Usage</span><Badge count={waPagination.total} showZero overflowCount={999} color="#64748b" /></Space>),
      children: (
        <Card className="wp-card" bordered={false}>
          <Table
            size="small"
            className="wp-compact-table"
            columns={waUsageColumns}
            dataSource={waUsage}
            loading={waUsageLoading}
            rowKey="campaignid"
            pagination={{ ...waPagination, showSizeChanger: true, showTotal: (total) => `${total} campaigns` }}
            onChange={(p) => { setWaPagination(prev => ({ ...prev, current: p.current, pageSize: p.pageSize })); fetchWaUsage(p.current, p.pageSize); }}
            scroll={{ x: 900 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No WhatsApp campaigns this cycle" /> }}
          />
        </Card>
      ),
    },
  ];

  /* ── Root Return ──────────────────────────── */
  return (
    <div className="wp-root">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        size="large"
        items={tabItems}
        tabBarExtraContent={
          <Space>
            <Button icon={<ReloadOutlined spin={refreshCooldown} />} onClick={handleRefresh} disabled={refreshCooldown} className="wp-btn-secondary">
              Refresh
            </Button>
          </Space>
        }
      />
    </div>
  );
};

export default WalletPage;
